import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableContainer,
  TableRow,
} from "@mui/material";

import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import SalesTableCell from "./SalesTableCell";
import SoftSelect from "components/SoftSelect";

const dashboardSelectSx = {
  width: 140,
  "--soft-select-height": "28px",
  "--soft-select-value-height": "26px",
  "--soft-select-font-size": "10px",
  "--soft-select-value-px": "8px",
  "--soft-select-text-top": "50%",
  "--soft-select-indicator-padding": "4px",
  "--soft-select-indicator-size": "12px",
  "--soft-select-menu-padding": "4px",
  "--soft-select-option-padding": "4px 8px",
  "--soft-select-border-radius": "6px",
};

const dashboardTitleSx = {
  fontSize: "12px !important",
  fontWeight: "400 !important",
  lineHeight: "1.15 !important",
};

function SalesTableDaily({ title, rows = [{}], footerData = null, onRangeChange, range }) {
  const handleChange = (value) => {
    if (onRangeChange) onRangeChange(value);
  };

  const footerCells = footerData
    ? [
        ["Grand Total", ""],
        ...(footerData.total_completes !== undefined
          ? [["Completes", new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(footerData.total_completes)]]
          : []),
        ...(footerData.total_revenue !== undefined
          ? [["Revenue", new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(footerData.total_revenue)]]
          : []),
        ["Percentage", `${Number(footerData.total_percentage ?? 100).toFixed(2)}%`],
      ]
    : [];

  const renderFooter = footerData ? (
    <TableRow
      sx={{
        backgroundColor: "#e0f2fe",
        position: "sticky",
        bottom: 0,
        width: "100%",
      }}
    >
      {footerCells.map(([title, content]) => (
        <SalesTableCell key={title} title={title} content={content} />
      ))}
    </TableRow>
  ) : null;

  return (
    <SoftBox sx={{ position: "relative", width: "100%" }}>
      <TableContainer
        sx={{
          maxHeight: "400px",
          overflowY: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {/*  Header with Title + Dropdown */}
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          py={0.75}
          borderBottom="1px solid #eee"
          backgroundColor="#fff"
        >
          <SoftTypography variant="h6" fontWeight="regular" sx={dashboardTitleSx}>
            {title}
          </SoftTypography>

          <SoftSelect
            size="small"
            compact
            sx={dashboardSelectSx}
            placeholder="Select"
            options={[
              { label: "24 Hours", value: "24h" },
              { label: "7 Days", value: "7d" },
            ]}
            onChange={(val) => handleChange(val.value)}
            value={
              [
                { label: "24 Hours", value: "24h" },
                { label: "7 Days", value: "7d" },
              ].find((opt) => opt.value === range) || ""
            }
          />
        </SoftBox>

        <Table stickyHeader>
          <TableBody>
            {rows.map((row, key) => {
              const tableCells = Object.entries(row).map(([cellTitle, cellContent]) => {
                if (Array.isArray(cellContent)) {
                  return (
                    <SalesTableCell
                      key={`cell-${cellTitle}`}
                      title={cellTitle}
                      content={cellContent[1]}
                      image={cellContent[0]}
                    />
                  );
                } else {
                  return (
                    <SalesTableCell
                      key={`cell-${cellTitle}`}
                      title={cellTitle}
                      content={cellContent}
                    />
                  );
                }
              });

              return <TableRow key={`row-${key}`}>{tableCells}</TableRow>;
            })}
          </TableBody>

          {footerData && <TableBody>{renderFooter}</TableBody>}
        </Table>
      </TableContainer>
    </SoftBox>
  );
}

SalesTableDaily.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  footerData: PropTypes.object,
  onRangeChange: PropTypes.func.isRequired,
  range: PropTypes.string.isRequired,
};

export default SalesTableDaily;
